// What a right-click on the timeline offers, by where it landed.
//
// DAWs agree on the shape of these menus, if not the wording. Live, Logic,
// Bitwig, Studio One and Reaper all split them by what is under the pointer:
//
//   - a clip        → the edit verbs (cut, copy, paste, duplicate, delete),
//                     split at the pointer, consolidate / glue, loop it, mute
//                     (Live: Deactivate), and a way into its editor;
//   - empty lane    → paste here, and on a MIDI track "Insert MIDI clip";
//   - a time span   → the same edit verbs acting on the span, split at its
//                     edges, consolidate, loop the selection;
//   - track header  → rename, mute / solo / arm, duplicate, delete, reorder;
//   - the ruler     → play from here, move the insert marker, the loop.
//
// The verbs are the ones the clip action bar and the keys already run
// (useClipOps), so a menu item, a button and a shortcut can never disagree —
// and each item names its key, as Live's do. ArrangementView has already made
// the clicked thing the selection and put the insert marker on the clicked
// beat by the time this is asked; Paste and Split act there.
//
// Pure: handed the place and what the page can do, it returns the entries.

import {
  ArrowDown,
  ArrowUp,
  ClipboardPaste,
  Combine,
  Copy,
  CopyPlus,
  CornerUpLeft,
  Disc,
  Headphones,
  ListChecks,
  MapPin,
  Pencil,
  Play,
  Plus,
  Repeat,
  Scissors,
  SquareSplitHorizontal,
  Trash2,
  VolumeX,
} from "lucide-react";
import type { ArrangementMenuTarget } from "@web-shared/components/ArrangementView";
import type { ContextMenuEntry } from "@web-shared/components/ui/context-menu";
import type { Lane } from "@web-shared/lib/arrangement";
import { stripTokens } from "@shared/sync/track-id.js";

/** ⌘ where the keys say ⌘, Ctrl where they say Ctrl. */
export const MOD =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? "⌘" : "Ctrl+";

export interface ArrangementMenuActions {
  /** The clip operations, as the action bar has them. */
  ops: {
    can: { copy: boolean; paste: boolean; split: boolean; act: boolean };
    muted: boolean;
    /** How many clips the operations act on. */
    selectionSize: number;
    clipboardSize: number;
    copy: () => void;
    cut: () => void;
    paste: () => void;
    duplicate: () => void;
    split: () => void;
    remove: () => void;
    toggleMute: () => void;
  };
  /** Live's Loop Selection, over the selection or span. */
  loopSelection: () => void;
  /** Live's Consolidate. Absent ⇒ not offered. */
  consolidate?: () => void;
  consolidating?: boolean;
  /** Open a clip in its editor (the clip editor, the piano roll). */
  openEditor?: (clipId: string) => void;
  /** An empty MIDI clip on this lane at this beat. */
  createMidiClip?: (laneId: string, atBeat: number) => void;
  /** An empty MIDI clip on this lane at the insert marker (the header's ＋). */
  addMidiClip?: (laneId: string) => void;
  /** Replace the selection with these clips. */
  selectClips: (clipIds: string[]) => void;
  /** The Set's own track flags. */
  trackFlag: (laneId: string, field: "mute" | "solo" | "arm", value: boolean) => void;
  deleteTrack?: (laneId: string) => void;
  duplicateTrack?: (laneId: string) => void;
  reorderTrack?: (laneId: string, direction: -1 | 1) => void;
  /** Live's per-track Back to Arrangement, for a lane the Session View has. */
  backToArrangement?: (laneId: string) => void;
  overridden?: ReadonlySet<string>;
  /** The ruler: play from a beat (jumps on the next bar while playing). */
  playFrom?: (beat: number) => void;
  /** The ruler: put the insert marker on a beat, without playing. */
  setInsert?: (beat: number) => void;
  /** The transport's loop switch. */
  loop?: { on: boolean; toggle: () => void };
  /** A beat as the ruler reads it (bars.beats.sixteenths) — the ruler menu's heading. */
  formatBeat?: (beat: number) => string;
  /** Shortcut prefix — ⌘ or Ctrl+. */
  mod?: string;
}

const SEP: ContextMenuEntry = { kind: "separator" };

/** "1.2.3"-free and short: whole beats as a count, the rest to three places. */
function beatsWord(n: number): string {
  const v = +n.toFixed(3);
  return `${v} beat${v === 1 ? "" : "s"}`;
}

/** The edit verbs, shared by a clip and a span — only their wording differs. */
function editVerbs(a: ArrangementMenuActions, mod: string, splitLabel: string): ContextMenuEntry[] {
  const { ops } = a;
  return [
    { id: "cut", label: "Cut", icon: Scissors, shortcut: `${mod}X`, disabled: !ops.can.copy, onSelect: ops.cut },
    { id: "copy", label: "Copy", icon: Copy, shortcut: `${mod}C`, disabled: !ops.can.copy, onSelect: ops.copy },
    {
      id: "paste",
      label: ops.clipboardSize > 1 ? `Paste ${ops.clipboardSize} clips` : "Paste",
      icon: ClipboardPaste,
      shortcut: `${mod}V`,
      disabled: !ops.can.paste,
      onSelect: ops.paste,
    },
    { id: "duplicate", label: "Duplicate", icon: CopyPlus, shortcut: `${mod}D`, disabled: !ops.can.copy, onSelect: ops.duplicate },
    SEP,
    { id: "split", label: splitLabel, icon: SquareSplitHorizontal, shortcut: `${mod}E`, disabled: !ops.can.split, onSelect: ops.split },
    ...(a.consolidate
      ? [{ id: "consolidate", label: a.consolidating ? "Consolidating…" : "Consolidate", icon: Combine, shortcut: `${mod}J`, disabled: !ops.can.act || a.consolidating === true, onSelect: a.consolidate }]
      : []),
    { id: "loop-selection", label: "Loop selection", icon: Repeat, shortcut: `${mod}L`, disabled: !ops.can.act, onSelect: a.loopSelection },
  ];
}

function trackFlags(lane: Lane, a: ArrangementMenuActions): ContextMenuEntry[] {
  return [
    { id: "track-mute", label: "Mute track", icon: VolumeX, checked: lane.mute, onSelect: () => a.trackFlag(lane.id, "mute", !lane.mute) },
    { id: "track-solo", label: "Solo track", icon: Headphones, checked: lane.solo, onSelect: () => a.trackFlag(lane.id, "solo", !lane.solo) },
    { id: "track-arm", label: "Arm for recording", icon: Disc, checked: lane.arm, onSelect: () => a.trackFlag(lane.id, "arm", !lane.arm) },
  ];
}

export function arrangementMenu(target: ArrangementMenuTarget, a: ArrangementMenuActions): ContextMenuEntry[] {
  const mod = a.mod ?? MOD;
  const { ops } = a;
  switch (target.kind) {
    case "clip": {
      const { clip, lane } = target;
      const many = ops.selectionSize > 1;
      const midi = clip.kind === "midi";
      return [
        { kind: "label", label: many ? `${ops.selectionSize} clips` : clip.name || (midi ? "MIDI clip" : "Audio clip") },
        ...(a.openEditor && !many
          ? [{ id: "open", label: midi ? "Edit notes" : "Edit clip", icon: Pencil, onSelect: () => a.openEditor?.(clip.id) }, SEP]
          : []),
        ...editVerbs(a, mod, "Split here"),
        SEP,
        { id: "mute-clip", label: many ? "Mute clips" : "Mute clip", icon: VolumeX, shortcut: "M", checked: ops.muted, disabled: !ops.can.act, onSelect: ops.toggleMute },
        ...(a.backToArrangement && a.overridden?.has(lane.id)
          ? [{ id: "back-to-arrangement", label: "Back to arrangement", icon: CornerUpLeft, onSelect: () => a.backToArrangement?.(lane.id) }]
          : []),
        SEP,
        { id: "delete", label: many ? `Delete ${ops.selectionSize} clips` : "Delete clip", icon: Trash2, shortcut: "⌫", danger: true, disabled: !ops.can.act, onSelect: ops.remove },
      ];
    }
    case "span": {
      const { span, lane } = target;
      const lanes = span.laneIds.length;
      return [
        { kind: "label", label: `${beatsWord(span.end - span.start)} · ${lanes} lane${lanes === 1 ? "" : "s"}` },
        ...editVerbs(a, mod, "Split at selection"),
        ...(lane.kind === "midi" && a.createMidiClip && lanes === 1
          ? [SEP, { id: "insert-midi", label: "Insert MIDI clip", icon: Plus, onSelect: () => a.createMidiClip?.(lane.id, span.start) }]
          : []),
        SEP,
        { id: "delete", label: "Delete selection", icon: Trash2, shortcut: "⌫", danger: true, disabled: !ops.can.act, onSelect: ops.remove },
      ];
    }
    case "lane": {
      const { lane, beat } = target;
      const clipIds = lane.clips.map((c) => c.id);
      return [
        { kind: "label", label: stripTokens(lane.name) || "Track" },
        {
          id: "paste",
          label: ops.clipboardSize > 1 ? `Paste ${ops.clipboardSize} clips here` : "Paste here",
          icon: ClipboardPaste,
          shortcut: `${mod}V`,
          disabled: !ops.can.paste,
          onSelect: ops.paste,
        },
        ...(lane.kind === "midi" && a.createMidiClip
          ? [{ id: "insert-midi", label: "Insert MIDI clip", icon: Plus, onSelect: () => a.createMidiClip?.(lane.id, beat) }]
          : []),
        SEP,
        { id: "select-track", label: "Select all on track", icon: ListChecks, disabled: clipIds.length === 0, onSelect: () => a.selectClips(clipIds) },
        SEP,
        ...trackFlags(lane, a),
      ];
    }
    case "track": {
      const { lane, rename } = target;
      const clipIds = lane.clips.map((c) => c.id);
      const total = lane.clips.length + lane.sessionClips.length;
      return [
        { kind: "label", label: stripTokens(lane.name) || "Track" },
        ...(rename ? [{ id: "rename", label: "Rename", icon: Pencil, onSelect: rename }] : []),
        SEP,
        ...trackFlags(lane, a),
        SEP,
        ...(lane.kind === "midi" && a.addMidiClip
          ? [{ id: "insert-midi", label: "Insert MIDI clip at marker", icon: Plus, onSelect: () => a.addMidiClip?.(lane.id) }]
          : []),
        { id: "select-track", label: "Select all clips", icon: ListChecks, disabled: clipIds.length === 0, onSelect: () => a.selectClips(clipIds) },
        ...(a.duplicateTrack ? [{ id: "duplicate-track", label: "Duplicate track", icon: CopyPlus, onSelect: () => a.duplicateTrack?.(lane.id) }] : []),
        ...(a.reorderTrack
          ? [
              { id: "move-up", label: "Move up", icon: ArrowUp, onSelect: () => a.reorderTrack?.(lane.id, -1) },
              { id: "move-down", label: "Move down", icon: ArrowDown, onSelect: () => a.reorderTrack?.(lane.id, 1) },
            ]
          : []),
        ...(a.backToArrangement && a.overridden?.has(lane.id)
          ? [{ id: "back-to-arrangement", label: "Back to arrangement", icon: CornerUpLeft, onSelect: () => a.backToArrangement?.(lane.id) }]
          : []),
        ...(a.deleteTrack
          ? [
              SEP,
              {
                id: "delete-track",
                label: total > 0 ? `Delete track and ${total} clip${total === 1 ? "" : "s"}` : "Delete track",
                icon: Trash2,
                danger: true,
                onSelect: () => a.deleteTrack?.(lane.id),
              },
            ]
          : []),
      ];
    }
    case "ruler": {
      const { beat } = target;
      return [
        { kind: "label", label: a.formatBeat ? `At ${a.formatBeat(beat)}` : "Timeline" },
        ...(a.playFrom ? [{ id: "play-from", label: "Play from here", icon: Play, onSelect: () => a.playFrom?.(beat) }] : []),
        ...(a.setInsert ? [{ id: "insert-marker", label: "Move insert marker here", icon: MapPin, onSelect: () => a.setInsert?.(beat) }] : []),
        ...(a.loop ? [SEP, { id: "loop", label: "Loop", icon: Repeat, checked: a.loop.on, onSelect: a.loop.toggle }] : []),
      ];
    }
  }
}
